import {
  Autocomplete,
  AutocompleteItem,
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@heroui/react';
import React, { useState } from 'react';

import {
  SUPPLIER_SEARCH_MIN_CHARS,
  useSupplierSearch,
  useWorkspaceCreate,
} from '../../hooks';

export interface CreateWorkspaceModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SEARCH_HINT = `Type at least ${SUPPLIER_SEARCH_MIN_CHARS} characters to search`;

export const CreateWorkspaceModal: React.FC<CreateWorkspaceModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { createWorkspace, isCreating, error, clearError } =
    useWorkspaceCreate();
  const { suppliers, isSearching, searchSuppliers } = useSupplierSearch();

  const [name, setName] = useState('');
  const [rootLocationId, setRootLocationId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const close = () => {
    clearError();
    setName('');
    setRootLocationId('');
    setSearchTerm('');
    onClose();
  };

  const submit = async () => {
    const created = await createWorkspace({ name, rootLocationId });
    if (created) {
      close();
    }
  };

  // The service rejects a blank name and an already-claimed root separately;
  // this only stops the obviously-empty submit.
  const canSubmit =
    name.trim().length > 0 && rootLocationId !== '' && !isCreating;

  return (
    <Modal isOpen={isOpen} onClose={close} size="lg">
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          Create workspace
          <span className="text-small font-normal text-default-500">
            A distributor can belong to only one workspace.
          </span>
        </ModalHeader>
        <ModalBody className="gap-4">
          <Input
            label="Name"
            placeholder="A name you will recognise in this list"
            value={name}
            onValueChange={setName}
            isDisabled={isCreating}
            variant="bordered"
            autoFocus
          />

          <Autocomplete
            label="Distributor"
            placeholder="Search distributors by name"
            description={
              searchTerm.length > 0 &&
              searchTerm.length < SUPPLIER_SEARCH_MIN_CHARS
                ? SEARCH_HINT
                : 'The workspace covers this distributor and everything under it.'
            }
            variant="bordered"
            isLoading={isSearching}
            isDisabled={isCreating}
            items={suppliers}
            selectedKey={rootLocationId || null}
            onInputChange={(value) => {
              setSearchTerm(value);
              searchSuppliers(value);
            }}
            onSelectionChange={(key) =>
              setRootLocationId(key ? String(key) : '')
            }
          >
            {(supplier) => (
              // textValue is set explicitly: with a composed child the input
              // would otherwise show the whole row once one is picked.
              <AutocompleteItem key={supplier.id} textValue={supplier.name}>
                <div className="flex flex-col">
                  <span className="text-sm text-foreground">
                    {supplier.name}
                  </span>
                  <span className="font-mono text-xs text-default-400">
                    Ref {supplier.referenceId}
                    {supplier.hierarchyType
                      ? ` · ${supplier.hierarchyType}`
                      : ' · Warehouse'}
                  </span>
                </div>
              </AutocompleteItem>
            )}
          </Autocomplete>

          {error && (
            <div className="rounded-medium bg-danger-50 p-3 text-small text-danger">
              {error}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="light" onPress={close} isDisabled={isCreating}>
            Cancel
          </Button>
          <Button
            color="primary"
            onPress={submit}
            isDisabled={!canSubmit}
            isLoading={isCreating}
          >
            Create
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
